import { type CellObserver, type Result, type ResultsObserver } from "./interfaces";

/**
 * Observes the a cell, keeping track of the last result.
 *
 * This is useful for when a React component in the UI layer needs to display the results of a cell,
 * but is unmounted for some reason. Once re-mounted, it can simply retrieve the cell's current
 * value and display that.
 */
export class CellResultsObserver implements CellObserver {
	public readonly setSubscriber = (observer: ResultsObserver) => {
		this._subscriber = observer;

		this._subscriber.next(this._currentResult);
	};

	public readonly resetStaticError = () => {
		const { staticError, ...rest } = this._currentResult;
		this._currentResult = rest;
	};

	//
	// CellObserver implementation.
	//

	public readonly initError = (staticError: Error) => {
		this._currentResult = { type: "PENDING", staticError, pending: false };
		this._subscriber?.next(this._currentResult);
	};
	public readonly pending = () => {
		this._currentResult = {
			type: "PENDING",
			staticError: this._currentResult.staticError,
			pending: true,
		};
		this._subscriber?.next(this._currentResult);
	};
	public readonly fulfilled = (value: unknown) => {
		this._currentResult = {
			type: "FULFILLED",
			staticError: this._currentResult.staticError,
			value,
		};
		this._subscriber?.next(this._currentResult);
	};
	public readonly rejected = (runtimeError: Error) => {
		this._currentResult = {
			type: "REJECTED",
			staticError: this._currentResult.staticError,
			runtimeError,
		};
		this._subscriber?.next(this._currentResult);
	};

	get currentResult() {
		return this._currentResult;
	}

	//
	// Private implementation.
	//

	private _subscriber: undefined | ResultsObserver;
	private _currentResult: Result = { type: "PENDING", pending: true };
}
